import {
  RoutePlanUnplannableAssignmentDTO,
  RoutePlanUnplannableReasonEnum,
} from 'api/model';
import useHasAccessToRoutePlanApply, {
  NO_ROUTE_PLAN_APPLY_ACCESS_TITLE,
} from 'hooks/useHasAccessToRoutePlanApply';
import React from 'react';
import styled from 'styled-components';
import { formatClock } from './selection-helpers';
import { getUnplannableReasonLabel } from './warning-helpers';
import { SeverityPill } from './WarningPills';
import CaseRegLink from './CaseRegLink';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
`;

const Heading = styled.h3`
  margin: 0;
  font-size: 1em;
`;

const Intro = styled.p`
  margin: 0;
  max-width: 90ch;
  font-size: 0.9em;
  color: rgb(90, 90, 90);
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;

  th {
    text-align: left;
    font-size: 0.8em;
    color: rgb(120, 120, 120);
    padding: 4px 8px;
    border-bottom: 1px solid rgb(190, 190, 190);
    white-space: nowrap;
  }

  td {
    padding: 7px 8px;
    border-bottom: 1px solid #b5b3b35c;
    vertical-align: top;
  }

  tbody tr:hover {
    background: #ecf6ff;
  }
`;

const Reg = styled.td`
  font-family: monospace;
  white-space: nowrap;
`;

const LegRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const LegText = styled.span`
  font-size: 0.92em;
`;

const Note = styled.div`
  font-size: 0.85em;
  color: rgb(90, 90, 90);
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.85em;
  color: rgb(30, 90, 160);
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    color: rgb(150, 150, 150);
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const GroupRow = styled.tr`
  td {
    padding-top: 12px;
    font-weight: 600;
    font-size: 0.9em;
    border-bottom: 1px solid rgb(190, 190, 190);
  }

  &:hover {
    background: none !important;
  }
`;

const Empty = styled.div`
  padding: 30px 0;
  text-align: center;
  color: rgb(120, 120, 120);
`;

export interface ReserveTarget {
  assignmentId: number;
  registrationNumber?: string;
  route: string;
  reserved: boolean;
  currentNote?: string;
}

interface Props {
  unplannableAssignments: RoutePlanUnplannableAssignmentDTO[];
  /**
   * Står det paketkort ovanför? Avgör bara tomtexten: utan kort är hela fliken tom och ska säga
   * att ingenting behöver planeraren i dag, med kort ovanför vore samma mening en lögn.
   */
  hasPackagesAbove: boolean;
  onReserve(target: ReserveTarget): void;
}

/** Lagret kan vara bokat en annan dag än planeringsdagen, så datumet behövs. */
const formatStockBooked = (value: Date) => {
  const date = new Date(value);
  return `${date.getDate()}/${date.getMonth() + 1} ${formatClock(value)}`;
};

/** Orten ur "adress, ort" — det är där bilen står, och det är på orten TL letar. */
const cityOf = (text?: string | null) => {
  const parts = (text ?? '').split(',');
  return parts[parts.length - 1].trim() || '—';
};

/**
 * Lager att flytta: vidaretransporter där inget senare ben i ärendet körs i dag. Uppspelningen
 * 2026-10-10 visade att nästan inga sådana kördes av en
 * fälttestare samma dag, så de planeras inte som egna paket. De kan fortfarande föreslås som
 * utfyllnad på en annan fälttestares väg, eller gå på lastbil.
 */
const StockSection: React.FC<{
  stock: RoutePlanUnplannableAssignmentDTO[];
}> = ({ stock }) => {
  const groups = new Map<string, RoutePlanUnplannableAssignmentDTO[]>();
  stock.forEach((a) => {
    const city = cityOf(a.fromText);
    groups.set(city, [...(groups.get(city) ?? []), a]);
  });
  const sorted = Array.from(groups.entries()).sort(
    ([cityA, a], [cityB, b]) =>
      b.length - a.length || cityA.localeCompare(cityB, 'sv')
  );

  return (
    <Wrapper>
      <Heading>Lager att flytta ({stock.length})</Heading>
      <Intro>
        Vidaretransporter där inget senare ben i ärendet körs i dag. De planeras
        inte som egna paket, men kan komma med som bil åt rätt håll när någon ändå åker
        åt det hållet — eller gå på lastbil. Bilar vars förra ben inte är kört
        än är märkta.
      </Intro>
      <Table>
        <thead>
          <tr>
            <th>Reg.nr</th>
            <th>Från → till</th>
            <th>Bokad</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(([city, rows]) => (
            <React.Fragment key={`stock-${city}`}>
              <GroupRow>
                <td colSpan={3}>
                  {city} · {rows.length} {rows.length === 1 ? 'bil' : 'bilar'}
                </td>
              </GroupRow>
              {rows
                .slice()
                .sort((a, b) =>
                  cityOf(a.toText).localeCompare(cityOf(b.toText), 'sv')
                )
                .map((a) => (
                  <tr key={`stock-assignment-${a.assignmentId}`}>
                    <Reg>
                      <CaseRegLink caseId={a.caseId}>
                        {a.registrationNumber || '—'}
                      </CaseRegLink>
                    </Reg>
                    <td>
                      <LegText>
                        {a.fromText} → {a.toText}
                      </LegText>
                      {/* Backend skriver "inte framme … än" när benet före inte är kört —
                          då står bilen inte i lagret ännu. */}
                      {a.reservationNote?.includes('inte framme') && (
                        <Note>Inte framme än — förra benet är inte kört</Note>
                      )}
                    </td>
                    <td>{formatStockBooked(a.bookedTo)}</td>
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </Wrapper>
  );
};

/**
 * Uppdrag som aldrig blev ett paket.
 *
 * Skild från paketkorten med flit: ett paket har kandidat, ben och luckor att välja på, och hör
 * därför hemma i samma kort som resten av förslaget. Raderna här har ingenting av det — de är
 * låsta av trafikledaren, saknar koordinat, eller har passerad tid — så det finns inget att rita
 * och inget val att erbjuda. Kvar är att visa att de finns: ett uppdrag som tyst uteblir ur
 * planeringen är omöjligt att skilja från ett fel.
 *
 * Ersatte paketdelen av den gamla <c>ManualListTable</c> 2026-09-22. Den listade även manuella
 * PAKET som rader, vilket gjorde att åtgärden de behövde — luckväljaren, bytet av fälttestare —
 * låg i en vy planeraren fick lämna för att kunna svara.
 */
const UnplannableTable: React.FC<Props> = ({
  unplannableAssignments,
  hasPackagesAbove,
  onReserve,
}) => {
  // Att släppa ett lås skriver till databasen, så det kräver samma behörighet som verkställ.
  const canReserve = useHasAccessToRoutePlanApply();

  // Lagret att flytta är inget fel att rätta, så det står för sig: vidaretransporter som inte
  // behövs i dag (TL-beslut 2026-10-11).
  const stock = unplannableAssignments.filter(
    (a) => a.reason === RoutePlanUnplannableReasonEnum.FlexibleStock
  );
  const rest = unplannableAssignments.filter(
    (a) => a.reason !== RoutePlanUnplannableReasonEnum.FlexibleStock
  );

  // "Ingenting behöver dig" gäller även när det bara finns lager — lagret väntar inte på något beslut.
  const nothingNeedsYou = rest.length === 0 && !hasPackagesAbove;
  if (rest.length === 0 && stock.length === 0) {
    return hasPackagesAbove ? null : (
      <Empty>
        Ingenting behöver dig i dag — hela förslaget är redo att tillämpas som
        det står.
      </Empty>
    );
  }

  return (
    <>
      {nothingNeedsYou && (
        <Empty>
          Ingenting behöver dig i dag — hela förslaget är redo att tillämpas som
          det står.
        </Empty>
      )}
      {rest.length > 0 && (
        <Wrapper>
          <Heading>Kom aldrig med i ett paket</Heading>
          <Intro>
            De här uppdragen nådde aldrig paketeringen, så det finns ingen rutt
            att ta ställning till. Låsta uppdrag släpps här, ben vid platser som
            alltid planeras manuellt planeras för hand, och övriga rättas på
            ärendet och kommer med i nästa körning.
          </Intro>
          <Table>
            <thead>
              <tr>
                <th>Reg.nr</th>
                <th>Uppdrag</th>
                <th>Bokad</th>
                <th>Varför den inte kunde planeras</th>
                <th>Nästa steg</th>
              </tr>
            </thead>
            <tbody>
              {rest.map((a) => {
                const reserved =
                  a.reason ===
                  RoutePlanUnplannableReasonEnum.ReservedForManualPlanning;
                // Backend skriver vilket ben som saknas i noteringen. Åtgärden är att planera det,
                // inte att rätta det här ärendet.
                const awaitsPredecessor =
                  a.reason ===
                  RoutePlanUnplannableReasonEnum.PredecessorLegUnplanned;
                // Platsen står i inställningen "Platser som alltid planeras manuellt" (t.ex.
                // KVD). Det finns inget lås att släppa och inget att rätta på ärendet.
                const atManualPlace =
                  a.reason ===
                  RoutePlanUnplannableReasonEnum.ManualPlanningPlace;
                return (
                  <tr key={`assignment-${a.assignmentId}`}>
                    <Reg>
                      <CaseRegLink caseId={a.caseId}>
                        {a.registrationNumber || '—'}
                      </CaseRegLink>
                    </Reg>
                    <td>
                      <LegRow>
                        <LegText>
                          {a.fromText} → {a.toText}
                        </LegText>
                        {reserved && (
                          <span
                            title={
                              canReserve
                                ? undefined
                                : NO_ROUTE_PLAN_APPLY_ACCESS_TITLE
                            }
                          >
                            <LinkButton
                              disabled={!canReserve}
                              onClick={() =>
                                onReserve({
                                  assignmentId: a.assignmentId,
                                  registrationNumber: a.registrationNumber,
                                  route: `${a.fromText} → ${a.toText}`,
                                  reserved: false,
                                  currentNote: a.reservationNote,
                                })
                              }
                              type="button"
                            >
                              Släpp
                            </LinkButton>
                          </span>
                        )}
                      </LegRow>
                    </td>
                    <td>{formatClock(a.bookedTo)}</td>
                    <td>
                      <SeverityPill
                        severity={reserved || atManualPlace ? 'info' : 'blocking'}
                      >
                        {getUnplannableReasonLabel(a.reason)}
                      </SeverityPill>
                    </td>
                    <td>
                      <Note>
                        {reserved
                          ? a.reservationNote
                            ? `“${a.reservationNote}” — utesluts ur poolen tills låset släpps`
                            : 'Utesluts ur poolen tills låset släpps'
                          : awaitsPredecessor
                            ? (a.reservationNote ??
                              'Planera förtransporten först, så planeras det här benet i nästa körning')
                            : atManualPlace
                              ? `${a.reservationNote ?? 'Platsen planeras alltid manuellt.'} Planera benet för hand.`
                              : 'Rätta det på ärendet så planeras det i nästa körning'}
                      </Note>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Wrapper>
      )}
      {stock.length > 0 && <StockSection stock={stock} />}
    </>
  );
};

export default UnplannableTable;
